import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Checkbox, IconButton, InputAdornment, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import { Clear, PrintOutlined, Search, SchoolOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { idCardService } from '../../services/idCardService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import type { EnrollmentListRow, SectionInstance, Session, SessionClass } from '../../types/sessions';
import { useSessionContext } from '../../context/SessionContext';
import { useDebounce } from '../../hooks/useDebounce';
import EmptyState from '../../components/common/EmptyState';
import FilterBar from '../../components/common/FilterBar';
import SearchableSelect, { type SelectOption } from '../../components/common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import { ToneChip } from '../../components/common/StatusChip';
import PersonCell from '../../components/people/PersonCell';
import { tableHeadCellSx } from '../../components/admissions/tabLayout';
import { fmtClassName } from '../../utils/pdf';
import { personFromEnrollment, type CardPerson } from '../../components/idCards/cardPeople';
import { useCardSelection } from '../../components/idCards/useCardSelection';
import CardsToolbar from '../../components/idCards/CardsToolbar';
import PrintCardsDialog, { MAX_PRINT } from '../../components/idCards/PrintCardsDialog';

const PAGE_FETCH = 200;
const PHOTO_OPTIONS: SelectOption[] = [{ value: 'missing', label: 'No photo' }];

/**
 * Student ID cards — find the students, tick some or take everyone the
 * filters match, and print their cards. The list is the session's
 * enrollments, so a card carries the student's current class and roll.
 */
const StudentIdCardsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { activeSession } = useSessionContext();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [withoutPhoto, setWithoutPhoto] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [rows, setRows] = useState<EnrollmentListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { selected, toggle, togglePage, clear } = useCardSelection();
  const [print, setPrint] = useState<{ load: () => Promise<CardPerson[]>; scope: string } | null>(null);

  useEffect(() => {
    Promise.all([sessionsApi.list(), classesApi.list()])
      .then(([s, c]) => { setSessions(s); setClasses(c); })
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the classes', { variant: 'error' }));
  }, [enqueueSnackbar]);
  useEffect(() => { if (!sessionId && activeSession) setSessionId(activeSession._id); }, [activeSession, sessionId]);

  useEffect(() => {
    if (!sessionId || !classId) { setSections([]); return; }
    let cancelled = false;
    sectionInstancesApi.list({ sessionId, classId })
      .then(s => { if (!cancelled) setSections(s); })
      .catch((err: any) => { if (!cancelled) enqueueSnackbar(err?.message || 'Could not load the sections', { variant: 'error' }); });
    return () => { cancelled = true; };
  }, [sessionId, classId, enqueueSnackbar]);

  // A new filter is a new list: start at its first page, nothing ticked.
  useEffect(() => { setPage(0); clear(); }, [sessionId, classId, sectionId, search, withoutPhoto, clear]);

  const sessionName = sessions.find(s => s._id === sessionId)?.name;
  const sessionOptions = useMemo(() => buildSessionOptions(sessions, theme), [sessions, theme]);
  const classOptions = useMemo(() => classes.map(c => ({ value: c._id, label: fmtClassName(c.name) })), [classes]);
  const sectionOptions = useMemo(() => sections.map(s => ({ value: s._id, label: s.name })), [sections]);
  const filter = useMemo(() => ({
    sessionId,
    classId: classId || undefined,
    sectionInstanceId: classId && sectionId ? sectionId : undefined,
    search: search.trim() || undefined,
    withoutPhoto: withoutPhoto || undefined,
  }), [sessionId, classId, sectionId, search, withoutPhoto]);

  const seq = useRef(0);
  useEffect(() => {
    if (!sessionId) return;
    const mine = ++seq.current;
    setLoading(true);
    idCardService.listStudents({ ...filter, page: page + 1, limit: rowsPerPage })
      .then(res => {
        if (mine !== seq.current) return;
        setRows(res.rows);
        setTotal(res.pagination?.totalItems ?? res.rows.length);
      })
      .catch((err: any) => { if (mine === seq.current) { enqueueSnackbar(err?.message || 'Could not load the students', { variant: 'error' }); setRows([]); } })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [sessionId, filter, page, rowsPerPage, enqueueSnackbar]);

  const pagePeople = useMemo(() => rows.map(r => personFromEnrollment(r, sessionName)), [rows, sessionName]);
  const pageTicked = pagePeople.length > 0 && pagePeople.every(p => selected.has(p.key));
  const pageSome = pagePeople.some(p => selected.has(p.key));

  /** Everyone the filters match, a page of 200 at a time. */
  const loadAll = useCallback(async () => {
    const out: CardPerson[] = [];
    for (let p = 1; out.length < MAX_PRINT; p++) {
      const res = await idCardService.listStudents({ ...filter, page: p, limit: PAGE_FETCH });
      out.push(...res.rows.map(r => personFromEnrollment(r, sessionName)));
      if (p >= (res.pagination?.totalPages ?? 1)) break;
    }
    return out;
  }, [filter, sessionName]);

  const scopeLabel = useMemo(() => {
    const cls = classes.find(c => c._id === classId);
    const sec = sections.find(s => s._id === sectionId);
    return [cls ? [fmtClassName(cls.name), sec?.name].filter(Boolean).join(' · ') : 'All classes', withoutPhoto ? 'without a photo' : '', sessionName]
      .filter(Boolean).join(' · ');
  }, [classes, classId, sections, sectionId, withoutPhoto, sessionName]);

  const filtersActive = !!classId || !!search.trim() || withoutPhoto;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Student ID cards</Typography>
        <Typography variant="body2" color="text.secondary">
          Print cards for a class, a section or the students you choose. Each card shows the student's current record.
        </Typography>
      </Box>

      <FilterBar loading={loading && rows.length > 0}>
        <TextField
          size="small"
          placeholder="Search by name"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
            endAdornment: searchInput ? (
              <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton></InputAdornment>
            ) : undefined,
          }}
        />
        <SearchableSelect label="Session" value={sessionId} onChange={setSessionId} options={sessionOptions} clearable={false} minWidth={210} />
        <SearchableSelect label="Class" placeholder="All classes" value={classId} onChange={v => { setClassId(v); setSectionId(''); }} options={classOptions} minWidth={160} />
        <SearchableSelect
          label="Section"
          placeholder={classId ? 'All sections' : 'Pick a class first'}
          value={sectionId}
          onChange={setSectionId}
          options={sectionOptions}
          disabled={!classId || sectionOptions.length === 0}
          minWidth={180}
        />
        <SearchableSelect label="Photo" placeholder="Any" value={withoutPhoto ? 'missing' : ''} onChange={v => setWithoutPhoto(v === 'missing')} options={PHOTO_OPTIONS} minWidth={160} />
      </FilterBar>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <CardsToolbar
          selectedCount={selected.size}
          total={total}
          noun={{ one: 'student', many: 'students' }}
          onClear={clear}
          onPrintSelected={() => { const list = Array.from(selected.values()); setPrint({ load: async () => list, scope: `${list.length} selected ${list.length === 1 ? 'student' : 'students'} · ${sessionName ?? ''}` }); }}
          onPrintAll={() => setPrint({ load: loadAll, scope: scopeLabel })}
        />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={pageTicked} indeterminate={!pageTicked && pageSome} onChange={() => togglePage(pagePeople)} inputProps={{ 'aria-label': 'Tick every student on this page' }} />
                </TableCell>
                <TableCell>Student</TableCell>
                <TableCell>GR No.</TableCell>
                <TableCell>Class</TableCell>
                <TableCell align="right">Roll</TableCell>
                <TableCell>Photo</TableCell>
                <TableCell width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRowSkeletons columns={7} rows={8} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<SchoolOutlined />}
                      title={filtersActive ? 'No students match' : 'No students enrolled in this session'}
                      description={filtersActive ? 'Try another class or name, or clear the filters.' : 'Students appear here once they are enrolled.'}
                      action={filtersActive ? { label: 'Clear filters', onClick: () => { setClassId(''); setSectionId(''); setSearchInput(''); setWithoutPhoto(false); } } : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.map((r, i) => {
                const person = pagePeople[i];
                const ticked = selected.has(person.key);
                return (
                  <TableRow key={r._id} hover selected={ticked} onClick={() => toggle(person)} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={ticked} inputProps={{ 'aria-label': `Tick ${person.name}` }} />
                    </TableCell>
                    <TableCell><PersonCell name={person.name} photoFilename={person.photoFilename} secondary={r.student?.fatherName ? `Father: ${r.student.fatherName}` : undefined} /></TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.student?.grNumber || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" noWrap>{person.values.classSection || '—'}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.rollNumber}</Typography></TableCell>
                    <TableCell>
                      {person.photoFilename ? <ToneChip label="On record" tone="success" /> : <ToneChip label="No photo" tone="warning" />}
                    </TableCell>
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Print this card">
                        <IconButton size="small" onClick={() => setPrint({ load: async () => [person], scope: person.name })} aria-label={`Print ${person.name}'s card`}>
                          <PrintOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {print && <PrintCardsDialog audience="student" loadPeople={print.load} scope={print.scope} onClose={() => setPrint(null)} />}
    </Box>
  );
};

export default StudentIdCardsPage;
